import BankPreAccountingApi from "./BankPreAccountingApi";
import { BankPreAccReAccountResult } from "../models/BankPreAccounting";
import {
  BankPreAccAccNo,
  BankPreAccDimCode,
  BankPreAccEditDimension,
  BankPreAccEditDimensionDefaultProps,
  BankPreAccEditItem,
  BankPreAccEditItemDefaultProps,
  BankPreAccEditorData,
  BankPreAccEditorFlags,
  BankPreAccEditorHead,
  BankPreAccEditorHeadDefaultProps,
  BankPreAccSaveRequest,
  BankPreAccSaveResult,
  BankPreAccStatementItem,
} from "../models/BankPreAccEditing";

// A banki kontír-szerkesztő singleton store-ja — a számla előkontírozás
// PreAccountingStore-jának bank-oldali, szűkített párja: tétel-felvétel és
// -törlés K2D-hűen nincs, ezért csak modified-követés van a tételekre;
// a dimenzió-bontás viszont teljes (új/módosított/törölt).
export class BankPreAccEditorStore {
  statementId = 0;

  head: BankPreAccEditorHead = { ...BankPreAccEditorHeadDefaultProps };
  originalAccStatus = 1;
  editable = false;
  reAccountable = false;
  uploadable = false;

  items: BankPreAccEditItem[] = [];
  originalItems: BankPreAccEditItem[] = [];
  modifiedItems: BankPreAccEditItem[] = [];

  statementItems: BankPreAccStatementItem[] = [];

  isItemsLoading = false;
  isSaving = false;
  // Az újrakontírozás saját jelzője: ne a Mentés gomb pörögjön, amikor az Újrakontírozásra kattintottak
  isReAccounting = false;
  isReadOnlyView = false;

  selectedItem: BankPreAccEditItem = { ...BankPreAccEditItemDefaultProps };
  positiveSelectedItem: BankPreAccEditItem = { ...BankPreAccEditItemDefaultProps };
  negativeSelectedItem: BankPreAccEditItem = { ...BankPreAccEditItemDefaultProps };

  dimensionsRows: BankPreAccEditDimension[] = [];
  originalDimensionsRows: BankPreAccEditDimension[] = [];
  newDimensions: BankPreAccEditDimension[] = [];
  modifiedDimensions: BankPreAccEditDimension[] = [];
  deletedDimensions: number[] = [];

  selectedDimension: BankPreAccEditDimension = { ...BankPreAccEditDimensionDefaultProps };
  positiveSelectedDimension: BankPreAccEditDimension = { ...BankPreAccEditDimensionDefaultProps };
  negativeSelectedDimension: BankPreAccEditDimension = { ...BankPreAccEditDimensionDefaultProps };
  positiveSelectedDimCodeType = 5;
  negativeSelectedDimCodeType = 5;

  accNos: BankPreAccAccNo[] = [];
  dimCodes: BankPreAccDimCode[] = [];
  detailedTypes: { id: number; name: string }[] = [];
  flags: BankPreAccEditorFlags = { extBook: false, dim49: false, checkDetType: false, upChangeable: false, dimTypeLive: {}, dimTypeRequired: {} };

  serCars: any[] = [];
  projects: any[] = [];
  pos: any[] = [];
  users: any[] = [];
  stations: any[] = [];
  tools: any[] = [];
  private dimLookupsLoaded = false;

  // Az új bontás-sorok negatív ideiglenes azonosítója
  private tempId = 0;

  nextTempId(): number {
    this.tempId--;
    return this.tempId;
  }

  get hasChanges(): boolean {
    return (
      this.modifiedItems.length > 0 ||
      this.newDimensions.length > 0 ||
      this.modifiedDimensions.length > 0 ||
      this.deletedDimensions.length > 0
    );
  }

  resetDeltas() {
    this.modifiedItems = [];
    this.newDimensions = [];
    this.modifiedDimensions = [];
    this.deletedDimensions = [];
    this.tempId = 0;
  }

  // Egy tétel „módosított"-státuszának karbantartása a 6 írható mező alapján
  syncModified(item: BankPreAccEditItem) {
    const original = this.originalItems.find((x) => x.id === item.id);
    if (!original) return;

    const fields: (keyof BankPreAccEditItem)[] = [
      "accCode", "detailedType", "dimCode1", "dimCode2", "dimCode3", "note",
    ];
    const changed = fields.some((key) => item[key] != original[key]);

    if (changed) {
      if (!this.modifiedItems.some((x) => x.id === item.id)) {
        this.modifiedItems.push(item);
      }
    } else {
      this.modifiedItems = this.modifiedItems.filter((x) => x.id !== item.id);
    }
  }

  // Egy bontás-sor „módosított"-státuszának karbantartása a MENTETT mezők alapján.
  // Egy sort két komponens is szerkeszt (dimenziókód/összeg a grid-wrapperben, a
  // dimenzió-azonosítók a fül-szerkesztőben), ezért a döntésnek EGY helyen és a
  // mezők teljes halmazán kell megtörténnie: külön-külön mezőlistával a két hívó
  // egymás módosítás-jelzését törölte a listáról.
  syncDimensionModified(dim: BankPreAccEditDimension) {
    const original = this.originalDimensionsRows.find((x) => x.id === dim.id);
    if (!original) return;

    const fields: (keyof BankPreAccEditDimension)[] = [
      "dimCode", "total",
      "extProdParts", "extProdPartner", "productParts", "production", "stock",
      "carId", "userId", "stationId", "toolId", "project", "poId",
    ];
    const changed = fields.some((key) => dim[key] != original[key]);

    if (changed) {
      if (!this.modifiedDimensions.some((x) => x.id === dim.id)) {
        this.modifiedDimensions.push(dim);
      }
    } else {
      this.modifiedDimensions = this.modifiedDimensions.filter((x) => x.id !== dim.id);
    }
  }

  // A kivonat-specifikus állapot ürítése. A lookupok (accNos, dimCodes, dim-fülek
  // törzsadatai) szándékosan maradnak: nem kivonathoz kötöttek, és a következő
  // sikeres betöltés amúgy is felülírja őket.
  resetEditorState() {
    this.head = { ...BankPreAccEditorHeadDefaultProps };
    this.originalAccStatus = 1;
    this.editable = false;
    this.reAccountable = false;
    this.uploadable = false;

    this.items = [];
    this.originalItems = [];
    this.statementItems = [];
    this.dimensionsRows = [];
    this.originalDimensionsRows = [];

    this.selectedItem = { ...BankPreAccEditItemDefaultProps };
    this.positiveSelectedItem = { ...BankPreAccEditItemDefaultProps };
    this.negativeSelectedItem = { ...BankPreAccEditItemDefaultProps };
    this.selectedDimension = { ...BankPreAccEditDimensionDefaultProps };
    this.positiveSelectedDimension = { ...BankPreAccEditDimensionDefaultProps };
    this.negativeSelectedDimension = { ...BankPreAccEditDimensionDefaultProps };

    this.resetDeltas();
  }

  async loadEditorData(statementId: number) {
    this.isItemsLoading = true;
    this.statementId = statementId;
    // A store singleton: ha a betöltés elhasal, a KORÁBBI kivonat adatai maradnának
    // a képernyőn tisztának hitt állapotban — ezért itt, a kérés előtt ürítünk.
    this.resetEditorState();
    try {
      const data: BankPreAccEditorData = await BankPreAccountingApi.getEditorData(statementId);
      this.head = data.head;
      this.originalAccStatus = data.originalAccStatus;
      this.editable = data.editable;
      this.reAccountable = data.reAccountable;
      this.uploadable = data.uploadable;
      this.statementItems = data.statementItems ?? [];

      // A DTO-kon nincsenek UI-flagek (selected/inEdit) — a defaultokkal egészítjük ki
      this.items = (data.items ?? []).map((x) => ({
        ...BankPreAccEditItemDefaultProps,
        ...x,
      }));
      this.originalItems = JSON.parse(JSON.stringify(this.items));
      this.dimensionsRows = (data.dimensions ?? []).map((x) => ({
        ...BankPreAccEditDimensionDefaultProps,
        ...x,
      }));
      this.originalDimensionsRows = JSON.parse(JSON.stringify(this.dimensionsRows));

      this.accNos = data.accNos;
      this.dimCodes = data.dimCodes;
      this.detailedTypes = data.detailedTypes;
      this.flags = data.flags;

      this.resetDeltas();
      this.selectedItem = { ...BankPreAccEditItemDefaultProps };
      this.positiveSelectedItem = { ...BankPreAccEditItemDefaultProps };
      this.negativeSelectedItem = { ...BankPreAccEditItemDefaultProps };
      this.selectedDimension = { ...BankPreAccEditDimensionDefaultProps };
    } finally {
      this.isItemsLoading = false;
    }
  }

  // A kísérő-lookupok (VII-X. dim fülek) — elég egyszer betölteni a szerkesztő életére
  async loadDimLookups() {
    if (this.dimLookupsLoaded) return;
    const [cars, projects, pos, users, stations, tools] = await Promise.all([
      BankPreAccountingApi.getEditorCars(),
      BankPreAccountingApi.getEditorProjects(),
      BankPreAccountingApi.getEditorPos(),
      BankPreAccountingApi.getEditorUsers(),
      BankPreAccountingApi.getEditorStations(),
      BankPreAccountingApi.getEditorTools(),
    ]);
    this.serCars = cars.map((x) => ({ ...x, idString: String(x.id) }));
    this.projects = projects.map((x) => ({ ...x, idString: String(x.id) }));
    this.pos = pos.map((x) => ({ ...x, idString: String(x.id) }));
    this.users = users;
    this.stations = stations;
    this.tools = tools;
    this.dimLookupsLoaded = true;
  }

  buildSavePayload(): BankPreAccSaveRequest {
    return {
      originalAccStatus: this.originalAccStatus,
      items: this.items.map((x) => ({
        id: x.id,
        accCode: x.accCode,
        detailedType: x.detailedType,
        dimCode1: x.dimCode1,
        dimCode2: x.dimCode2,
        dimCode3: x.dimCode3,
        note: x.note,
      })),
      dimensions: this.dimensionsRows.map((d) => ({
        id: d.id,
        head: d.head,
        dimCodeType: d.dimCodeType,
        dimCode: d.dimCode,
        total: Number(d.total) || 0,
        project: d.project,
        poId: d.poId,
        userId: d.userId,
        stationId: d.stationId,
        toolId: d.toolId,
        carId: d.carId,
        extProdParts: d.extProdParts,
        extProdPartner: d.extProdPartner,
        productParts: d.productParts,
        production: d.production,
        stock: d.stock,
      })),
      deletedDimensionIds: [...this.deletedDimensions],
    };
  }

  async save(): Promise<BankPreAccSaveResult> {
    this.isSaving = true;
    try {
      return await BankPreAccountingApi.save(this.statementId, this.buildSavePayload());
    } finally {
      this.isSaving = false;
    }
  }

  // K2D pb_recontir: a kivonat újrakontírozása. SAJÁT végpont, nem a böngésző
  // csoportosa 1 elemmel — a K2D-ben itt a 3-as "Feladható" státusz is mehet.
  async reAccount(): Promise<BankPreAccReAccountResult> {
    this.isReAccounting = true;
    try {
      return await BankPreAccountingApi.reAccountSingle(this.statementId);
    } finally {
      this.isReAccounting = false;
    }
  }

  async isValidPartCode(partCode: string) {
    return await BankPreAccountingApi.validatePart(partCode);
  }

  async ValidPartnerName(partnerName: string) {
    return await BankPreAccountingApi.validatePartner(partnerName);
  }
}

const store = new BankPreAccEditorStore();
export default store;
